import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { EMPTY_VALUE, formatCurrency, formatNumber } from "@/i18n/format";
import { useModeratorsCompaniesCompanyInformationRetrieve } from "@/services/moderators/generated/moderators-companies/moderators-companies";
import { useModeratorsInformationRetrieve } from "@/services/moderators/generated/moderators-moderators/moderators-moderators";
import { useModeratorsReadinessSummaryRetrieve } from "@/services/moderators/generated/moderators-readiness/moderators-readiness";
import "./ModeratorDashboardPage.css";

const formatGrowth = (value: number | undefined): string => {
  if (value === undefined || Number.isNaN(value)) return EMPTY_VALUE;
  return formatNumber(value / 100, {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  });
};

const growthClass = (value: number | undefined): string => {
  if (value === undefined || value === 0) return "moderator-dashboard__growth--neutral";
  return value > 0 ? "moderator-dashboard__growth--up" : "moderator-dashboard__growth--down";
};

const ModeratorDashboardPage = () => (
  <ModeratorLayout>
    {(moderator) => {
      const isSuper = moderator.role === "SUPERMODERATOR";
      return <DashboardContent isSuper={isSuper} fullName={moderator.fullName} />;
    }}
  </ModeratorLayout>
);

const DashboardContent = ({ isSuper, fullName }: { isSuper: boolean; fullName: string }) => {
  const { t } = useTranslation("moderator");
  const { data: info, isLoading: infoLoading, error: infoError } = useModeratorsInformationRetrieve();
  const {
    data: companyInfo,
    isLoading: companyInfoLoading,
    error: companyInfoError,
  } = useModeratorsCompaniesCompanyInformationRetrieve();

  const metricsLoading = infoLoading || companyInfoLoading;
  const metricsError = infoError || companyInfoError;

  // WP2.1 — readiness tile. Shown to every moderator; link opens the
  // management page.
  const { data: readinessSummary } = useModeratorsReadinessSummaryRetrieve();
  const summaryRecord = (readinessSummary ?? {}) as Record<string, unknown>;
  const activatedCompanies = (summaryRecord.activatedCompanies ?? 0) as number;
  const byBand = (summaryRecord.byBand ?? []) as { band: string; count: number }[];
  const activeQuestions = (summaryRecord.activatedQuestions ?? 0) as number;
  const bandLabel = (band: string): string => {
    switch (band) {
      case "ready":
        return t("readiness.bands.ready");
      case "developing":
        return t("readiness.bands.developing");
      case "not_ready":
        return t("readiness.bands.not_ready");
      default:
        return t("readiness.bands.none");
    }
  };

  return (
    <div className="moderator-dashboard">
      <header className="moderator-dashboard__page-header">
        <h1 className="moderator-dashboard__title">{t("dashboard.welcome", { name: fullName })}</h1>
        <p className="moderator-dashboard__subtitle">
          {isSuper ? t("dashboard.subtitleSuper") : t("dashboard.subtitle")}
        </p>
      </header>

      {metricsError ? (
        <div className="moderator-dashboard__error" role="alert">
          {t("dashboard.loadError")}
        </div>
      ) : null}

      <section className="moderator-dashboard__grid" aria-label={t("dashboard.platformMetrics")}>
        <article className="moderator-dashboard__card">
          <h2 className="moderator-dashboard__card-label">{t("dashboard.totalCompanies")}</h2>
          <p className="moderator-dashboard__card-value">{metricsLoading ? "…" : (info?.totalCompanies ?? "—")}</p>
          <p className="moderator-dashboard__card-meta">
            {t("dashboard.companiesMeta", {
              verified: companyInfo?.verifiedCompanies ?? 0,
              pending: companyInfo?.pendingCompanies ?? 0,
              flagged: companyInfo?.flaggedCompanies ?? 0,
            })}
          </p>
        </article>

        <article className="moderator-dashboard__card">
          <h2 className="moderator-dashboard__card-label">{t("dashboard.conversionRate")}</h2>
          <p className="moderator-dashboard__card-value">
            {metricsLoading
              ? "…"
              : info?.conversionRate !== undefined
                ? formatNumber(info.conversionRate / 100, {
                    style: "percent",
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })
                : EMPTY_VALUE}
          </p>
          <p className={`moderator-dashboard__card-meta ${growthClass(info?.conversionRateGrowth)}`}>
            {t("dashboard.vsLastMonth", { value: formatGrowth(info?.conversionRateGrowth) })}
          </p>
        </article>

        <article className="moderator-dashboard__card">
          <h2 className="moderator-dashboard__card-label">{t("dashboard.dealVolume")}</h2>
          <p className="moderator-dashboard__card-value">
            {metricsLoading
              ? "…"
              : info?.dealVolume !== undefined
                ? formatCurrency(Math.round(info.dealVolume), "USD", { maximumFractionDigits: 0 })
                : EMPTY_VALUE}
          </p>
          <p className={`moderator-dashboard__card-meta ${growthClass(info?.dealVolumeGrowth)}`}>
            {t("dashboard.vsLastMonth", { value: formatGrowth(info?.dealVolumeGrowth) })}
          </p>
        </article>

        <article className="moderator-dashboard__card">
          <h2 className="moderator-dashboard__card-label">{t("dashboard.avgResponseTime")}</h2>
          <p className="moderator-dashboard__card-value">{metricsLoading ? "…" : (info?.averageResponseTime ?? "—")}</p>
          <p className={`moderator-dashboard__card-meta ${growthClass(info?.averageResponseTimeGrowth)}`}>
            {t("dashboard.vsLastMonth", { value: formatGrowth(info?.averageResponseTimeGrowth) })}
          </p>
        </article>
      </section>

      <section
        className="moderator-dashboard__grid moderator-dashboard__grid--companies"
        aria-label={t("dashboard.companyBreakdown")}
      >
        <article className="moderator-dashboard__card moderator-dashboard__card--accent">
          <h2 className="moderator-dashboard__card-label">{t("dashboard.verified")}</h2>
          <p className="moderator-dashboard__card-value">
            {metricsLoading ? "…" : (companyInfo?.verifiedCompanies ?? "—")}
          </p>
        </article>
        <article className="moderator-dashboard__card moderator-dashboard__card--accent">
          <h2 className="moderator-dashboard__card-label">{t("dashboard.pendingApproval")}</h2>
          <p className="moderator-dashboard__card-value">
            {metricsLoading ? "…" : (companyInfo?.pendingCompanies ?? "—")}
          </p>
        </article>
        <article className="moderator-dashboard__card moderator-dashboard__card--accent">
          <h2 className="moderator-dashboard__card-label">{t("dashboard.flagged")}</h2>
          <p className="moderator-dashboard__card-value">
            {metricsLoading ? "…" : (companyInfo?.flaggedCompanies ?? "—")}
          </p>
        </article>
      </section>

      <section className="moderator-dashboard__grid" aria-label={t("readiness.dashboard.title")}>
        <article className="moderator-dashboard__card moderator-dashboard__card--accent">
          <h2 className="moderator-dashboard__card-label">{t("readiness.dashboard.activatedCompanies")}</h2>
          <p className="moderator-dashboard__card-value">{activatedCompanies}</p>
          <p className="moderator-dashboard__card-meta">{t("readiness.dashboard.activatedHint")}</p>
        </article>
        {byBand.map((row) => (
          <article key={row.band} className="moderator-dashboard__card">
            <h2 className="moderator-dashboard__card-label">{bandLabel(row.band)}</h2>
            <p className="moderator-dashboard__card-value">{row.count}</p>
          </article>
        ))}
        <article className="moderator-dashboard__card">
          <h2 className="moderator-dashboard__card-label">{t("readiness.dashboard.activeQuestions")}</h2>
          <p className="moderator-dashboard__card-value">{activeQuestions}</p>
          <p className="moderator-dashboard__card-meta">
            <Link to="/moderator/readiness">{t("readiness.dashboard.viewAll")}</Link>
          </p>
        </article>
      </section>
    </div>
  );
};

export default ModeratorDashboardPage;
